// composables
import { ref, watch, reactive, type Ref } from 'vue'
// utilities
import { alertToast } from '@/utilities/notification'
import Swal from 'sweetalert2'
import { useRouter } from 'vue-router'
import { MeiliSearch } from 'meilisearch'
// composables
import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'

/**
 * Composable for managing agreement merge operations
 * Handles MeiliSearch integration, validation, and merge submission
 */
export default function useAgreementMerge(agreementID: Ref<string | number>, dealershipIDs: Ref<number[]>) {
  const router = useRouter()

  // state
  const mergeModalIsOpen = ref(false)
  const searchTerm = ref('')
  const meiliSearchResults = ref<{ hits?: Array<Record<string, unknown>> }>({})
  const meilisearchClient = ref<MeiliSearch | null>(null)
  const agreementsList = ref<Array<Record<string, unknown>>>([])

  const mergeAgreementData = reactive({
    from: {
      agreementID: parseInt(String(agreementID.value))
    },
    to: null as number | null
  })

  // api requests
  const mergeAgreement = useApiRequest()

  // validation
  const mergeRules = {
    from: {
      agreementID: { required }
    },
    to: {
      required,
      mergeToIsValid: helpers.withMessage('Cannot merge into itself', value => {
        return value != mergeAgreementData.from
      })
    }
  }

  const mergeV$ = useVuelidate(mergeRules, mergeAgreementData)

  /**
   * Initializes MeiliSearch client
   */
  function initMeiliSearch(): void {
    meilisearchClient.value = new MeiliSearch({
      host: import.meta.env.VITE_MEILISEARCH_URL,
      apiKey: import.meta.env.VITE_MEILISEARCH_API_KEY
    })
  }

  /**
   * Searches agreements using MeiliSearch
   * @param query - Search query string
   */
  async function search(query: string): Promise<void> {
    if (!meilisearchClient.value) {
      initMeiliSearch()
    }

    if (query) {
      meiliSearchResults.value = await meilisearchClient.value!.index('agreements').search(query, {
        filter: 'dealershipId IN [' + dealershipIDs.value.join(',') + ']'
      })

      if (meiliSearchResults.value.hits != undefined) {
        agreementsList.value = meiliSearchResults.value.hits
      }
    } else {
      meiliSearchResults.value = {}
      agreementsList.value = []
    }
  }

  /**
   * Watches search term and triggers search
   */
  watch(searchTerm, newSearchTerm => {
    search(newSearchTerm)
  })

  /**
   * Submits merge agreement request
   */
  async function submitMergeAgreement(): Promise<void> {
    // Manual validation since Vuelidate seems to have conflicts with reactive objects
    if (!mergeAgreementData.from?.agreementID) {
      alertToast('Validation failed', 'Source agreement ID is missing', 'error')
      return
    }

    if (!mergeAgreementData.to) {
      alertToast('Validation failed', 'Please select an agreement to merge into', 'error')
      return
    }

    if (mergeAgreementData.to === mergeAgreementData.from.agreementID) {
      alertToast('Validation failed', 'Cannot merge into itself', 'error')
      return
    }

    // Validate with Vuelidate for UI error display, but don't block on it
    await mergeV$.value.$validate()

    Swal.fire({
      title: 'Are you sure?',
      text: 'This cannot be reversed. Please triple check before merging.',
      icon: 'warning',
      reverseButtons: true,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Confirm Merge',
      confirmButtonColor: 'var(--bg-brand-solid)',
      cancelButtonText: 'Cancel',
      cancelButtonColor: 'var(--bg-secondary)'
    }).then(function (response) {
      if (response.isConfirmed) {
        const jsonData = JSON.stringify({
          to: mergeAgreementData.to
        })

        mergeAgreement
          .send({
            endpoint: '/v1/agreements/' + agreementID.value + '/merge',
            method: 'PATCH',
            data: jsonData
          })
          .then(response => {
            if (response.success) {
              alertToast(
                'Agreement Merged',
                'Agreement #' + agreementID.value + ' has been merged into #' + mergeAgreementData.to,
                'success'
              )
              router.replace('/agreement/' + mergeAgreementData.to)
            } else {
              alertToast('Failed to merge agreement #' + agreementID.value, response.message ?? undefined, 'error')
            }
            // Close modal
            mergeModalIsOpen.value = false
          })
      }
    })
  }

  return {
    // state
    mergeModalIsOpen,
    mergeAgreementData,
    searchTerm,
    agreementsList,
    mergeV$,
    mergeAgreement,
    // methods
    initMeiliSearch,
    search,
    submitMergeAgreement
  }
}
